import React, { useRef } from "react";
import { Button } from "../../components/Common/AppOverlay";
export default function EngagementUploadStep({
  file,
  error,
  headerRowError,
  structureNotice,
  onUseOriginal,
  onFile,
  onRemove,
  sheets,
  sheet,
  onSheet,
  headerRow,
  previewRows = [],
  onHeaderRow,
  busy,
  detectedStructure,
  headingReviewConfirmed,
  onConfirmHeadings,
  dateOrder,
  onDateOrder,
}) {
  const fileInput = useRef(null);

  const removeFile = () => {
    if (fileInput.current) fileInput.current.value = "";
    onRemove();
  };
  return (
    <section className="engagement-import__workspace engagement-import__own-upload">
      <small className="engagement-import__ai-badge">AI-assisted import</small>
      <h2>Upload your engagement file</h2>
      <p>
        Upload your existing engagement spreadsheet or PDF—no PIERS template
        required. AI identifies Excel table structure and PDF columns and rows and suggests how your columns
        map to PIERS fields. You’ll review the mappings and resolve any warnings
        before importing.
      </p>
      <p>
        Excel worksheet samples and PDF table text are processed by PIERS’s configured Azure AI service.
        Upload only documents your firm permits for this processing. Check the
        extracted rows against your original document before importing.
      </p>
      <div
        className="engagement-import__drop-zone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!busy) onFile(event.dataTransfer.files[0]);
        }}
      >
        <i className="bx bx-cloud-upload" aria-hidden="true" />
        <strong>Drag and drop your engagement file here</strong>
        <span>or browse files</span>
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.csv,.pdf"
          hidden
          disabled={busy}
          onChange={(event) => onFile(event.target.files[0])}
        />
        <Button
          type="button"
          color="primary"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        >
          <i className="bx bx-upload" aria-hidden="true" /> Choose File
        </Button>
        <small>
          Excel (.xlsx), CSV or PDF • Maximum 10 MB • Up to 10,000 engagements
        </small>
        <small>
          PDFs must have selectable text and column headings. Multiline headings
          are supported. Scanned PDFs need OCR first. Split large PDFs into smaller files.
        </small>
      </div>
      {busy && <p role="status">Analyzing your file. Excel structure detection and PDF extraction may take a minute.</p>}
      {error && <p role="alert">{error}</p>}
      {file && structureNotice && <p role="status" className="alert alert-info">{structureNotice}</p>}
      {file && onUseOriginal && <Button type="button" outline disabled={busy} onClick={onUseOriginal}>Review original worksheets</Button>}
      {file && (
        <div className="engagement-import__source-options">
          <strong>{file.name}</strong>
          <Button
            type="button"
            outline
            color="danger"
            className="engagement-import__remove-file"
            disabled={busy}
            onClick={removeFile}
          >
            <i className="bx bx-trash" aria-hidden="true" /> Remove file
          </Button>
          <label>
            {/\.pdf$/i.test(file.name) ? "Extracted table" : "Worksheet"}
            <select
              value={sheet}
              disabled={busy}
              onChange={(event) => onSheet(event.target.value)}
            >
              {sheets.map((name) => (
                <option key={name}>{name}</option>
              ))}
            </select>
          </label>
          <label>
            Header row (column headings)
            <input
              id="engagement-header-row"
              type="number"
              min="1"
              max="100"
              step="1"
              aria-invalid={!!headerRowError}
              aria-describedby={`engagement-header-help${headerRowError ? " engagement-header-error" : ""}`}
              className={headerRowError ? "is-invalid" : undefined}
              value={headerRow}
              disabled={busy}
              onChange={(event) => onHeaderRow(event.target.value === "" ? "" : Number(event.target.value))}
            />
            <small id="engagement-header-help">
              Enter the worksheet row containing column names, such as Client Name and Engagement Name.
              For headings on row 5, enter 5. This is not the number of engagements.
              Use a whole number from 1 to 100; engagement data is read from the rows below.
            </small>
            {headerRowError && <small id="engagement-header-error" className="text-danger" role="alert">{headerRowError}</small>}
          </label>
        </div>
      )}
      {file && onDateOrder && (
        <label>
          Date order for slash dates
          <select value={dateOrder} disabled={busy} onChange={(event) => onDateOrder(event.target.value)}>
            <option value="">Choose when dates are ambiguous</option>
            <option value="DMY">Day / Month / Year</option>
            <option value="MDY">Month / Day / Year</option>
          </select>
          <small>Applies to slash dates throughout this file. Dates such as 04/05/2026 require a choice. Native Excel dates and ISO dates are unaffected.</small>
        </label>
      )}
      {file && detectedStructure?.headerEnd > detectedStructure?.headerStart && (
        <div style={{ overflowX: "auto" }}>
          <p>Review every detected heading row. These rows will not become engagements. If any row contains engagement data, select Review original worksheets and choose the correct header row.</p>
          <table className="table" aria-label="Detected heading rows">
            <tbody>{detectedStructure.headingRows?.map((row, index) => (
              <tr key={index}><th>Source {detectedStructure.orientation === "columns" ? "column" : "row"} {detectedStructure.headerStart + index}</th>{row.map((value, col) => <td key={col}>{String(value ?? "")}</td>)}</tr>
            ))}</tbody>
          </table>
          <label><input type="checkbox" checked={!!headingReviewConfirmed} disabled={busy} onChange={(event) => onConfirmHeadings(event.target.checked)} /> I confirm these rows contain column headings only, not engagement data.</label>
        </div>
      )}
      {file && previewRows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <p>
            Choose the row containing column names, not engagement data. Check
            the selected header and sample rows below.
          </p>
          <table className="table" aria-label="Selected header and sample rows">
            <thead>
              <tr>
                <th>Worksheet row</th>
                {(previewRows[0] || []).map((value, index) => (
                  <th key={index}>{String(value || "(blank)")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.slice(1).map((row, index) => (
                <tr key={index}>
                  <th>{headerRow + index + 1}</th>
                  {(previewRows[0] || []).map((_, col) => (
                    <td key={col}>{String(row[col] ?? "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p>Selected header: worksheet row {headerRow}.</p>
        </div>
      )}
    </section>
  );
}
